import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { X, Zap, TrendingUp, Sparkles } from 'lucide-react';
import { eventsApi } from '../services/api';

const ACCENT = '#00FF88';

/**
 * Server-decided upgrade prompt.
 *
 * Component contract:
 *   - Caller passes `context` ("dashboard" or "in_room"). We ask the server
 *     should-show endpoint; if `show:false` we render nothing.
 *   - On render we fire `upgrade_prompt_shown` (server records the 72-h
 *     debounce inside the events route).
 *   - On click of the primary CTA we fire `upgrade_clicked` and navigate to
 *     /pricing.
 *   - Variant copy is rendered server-decided ('contextual' vs 'dashboard').
 *
 * The component is INTENTIONALLY non-modal. Per the engineering brief,
 * graduation prompts are contextual nudges, never interrupting flow.
 */
const VARIANT_COPY = {
  contextual: {
    headline: 'You\'re shipping fast — keep the momentum',
    body: 'You\'ve been hands-on in the sandbox. Pro removes the timer and unlocks AI threat analysis + certificates.',
    cta: 'Continue with Pro — $15/mo',
    icon: TrendingUp,
  },
  dashboard: {
    headline: 'Unlock unlimited sandbox time',
    body: 'Your score puts you in the top tier of engaged learners. Pro removes monthly caps and unlocks certificates.',
    cta: 'Upgrade to Pro — $15/mo',
    icon: Sparkles,
  },
};

const UpgradePrompt = ({ context }) => {
  const navigate = useNavigate();
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/pql/should-show-prompt?context=${encodeURIComponent(context)}`;
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) {
          if (!cancelled) setLoading(false);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        setDecision(data);
        setLoading(false);

        // Fire upgrade_prompt_shown only after the component actually
        // becomes visible. The server uses this to set the 72-h debounce.
        if (data.show) {
          eventsApi.track('upgrade_prompt_shown', {
            trigger_source: context,
            context,
            ab_variant: data.ab_variant,
            score: data.score,
          });
        }
      } catch (err) {
        console.debug('[upgrade-prompt] should-show failed:', err?.message);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [context]);

  const handleClick = useCallback(() => {
    if (!decision?.show) return;
    eventsApi.track('upgrade_clicked', {
      trigger_source: context,
      context,
      ab_variant: decision.ab_variant,
    });
    navigate('/pricing');
  }, [context, decision, navigate]);

  if (loading || !decision?.show || dismissed) return null;

  const variant = VARIANT_COPY[decision.ab_variant] || VARIANT_COPY.dashboard;
  const Icon = variant.icon;

  return (
    <Card
      data-testid={`upgrade-prompt-${decision.ab_variant}`}
      className="border bg-[#0F1A12] relative overflow-hidden"
      style={{ borderColor: 'rgba(0,255,136,0.4)' }}
    >
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        data-testid="upgrade-prompt-dismiss"
        className="absolute top-2 right-2 text-gray-500 hover:text-white p-1 rounded"
      >
        <X className="w-4 h-4" />
      </button>
      <CardContent className="py-4">
        <div className="flex items-start gap-3 pr-6">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(0,255,136,0.15)' }}
          >
            <Icon className="w-5 h-5" style={{ color: ACCENT }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white text-sm font-semibold mb-1">
              {variant.headline}
            </h3>
            <p className="text-xs text-gray-400 mb-3">{variant.body}</p>
            <Button
              data-testid="upgrade-prompt-cta"
              size="sm"
              onClick={handleClick}
              className="text-black font-semibold"
              style={{ backgroundColor: ACCENT }}
            >
              <Zap className="w-3.5 h-3.5 mr-1.5" />
              {variant.cta}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UpgradePrompt;
